.trigger {
  box-sizing: border-box;
  height: var(--button-height);
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: 0 var(--space-4);
  border: 1px solid var(--color-focus-border);
  border-radius: var(--button-radius);
  background: var(--color-surface);
  color: var(--color-primary);
  font-family: var(--font-family-base);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  white-space: nowrap;
}

.trigger:hover {
  background: var(--color-surface-subtle);
}

.triggerIcon {
  font-size: var(--font-size-md);
  line-height: 1;
}

.count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 var(--space-1);
  border-radius: var(--radius-full);
  background: var(--color-primary-subtle);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
}

.chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 150ms ease;
}

.chevronOpen {
  transform: rotate(180deg);
}

.positioner {
  z-index: 20;
}

.popup {
  box-sizing: border-box;
  width: min(680px, calc(100vw - var(--space-8)));
  max-height: min(720px, calc(100vh - var(--space-12) - var(--space-8)));
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  box-shadow:
    0 18px 48px var(--color-box-shadow-bold),
    0 4px 12px var(--color-box-shadow-subtle);
  color: var(--color-text-dark);
  overflow: hidden;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-5) var(--space-6) var(--space-4);
}

.title {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
}

.headerActions {
  display: inline-flex;
  align-items: center;
  gap: var(--space-4);
}

.clearButton {
  border: 0;
  background: transparent;
  color: var(--color-primary);
  font-family: var(--font-family-base);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
}

.clearButton:hover {
  color: var(--color-primary-hover);
}

.iconButton {
  width: var(--space-8);
  height: var(--space-8);
  border: 0;
  border-radius: var(--radius-full);
  background: transparent;
  color: var(--color-text-muted);
  font-size: var(--font-size-xl);
  line-height: 1;
  cursor: pointer;
}

.iconButton:hover {
  background: var(--color-surface-subtle);
}

.section {
  padding: 0 var(--space-6) var(--space-5);
}

.section + .section {
  border-top: 1px solid var(--color-border);
  padding-top: var(--space-4);
}

.sectionLabel {
  margin-bottom: var(--space-3);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-dark);
}

.degreeGroup {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4) var(--space-8);
}

.checkboxRow,
.checkboxRowInline {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  color: var(--color-text-dark);
  font-size: var(--font-size-md);
  cursor: pointer;
}

.checkboxRow {
  min-height: 1.9rem;
}

.checkbox {
  box-sizing: border-box;
  width: 1.125rem;
  height: 1.125rem;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid var(--color-text-muted);
  border-radius: calc(var(--radius-sm) / 2);
  background: var(--color-surface);
  color: var(--color-primary-text);
}

.checkbox[data-checked] {
  border-color: var(--color-primary);
  background: var(--color-primary);
}

.indicator {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  line-height: 1;
}

.searchWrapper {
  box-sizing: border-box;
  height: calc(var(--input-height) - var(--space-2));
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
  padding: 0 var(--space-3);
  border: 1px solid var(--color-unfocus-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
}

.searchWrapper:focus-within {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-focus-ring);
}

.searchIcon {
  color: var(--color-text-muted);
  font-size: var(--font-size-md);
}

.searchInput {
  min-width: 0;
  width: 100%;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--color-text-dark);
  font: inherit;
}

.searchInput::placeholder {
  color: var(--color-text-muted);
}

.subfieldList {
  max-height: 310px;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding-right: var(--space-2);
  overflow-y: auto;
}

.emptyState {
  padding: var(--space-3) 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
}

.footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-6);
  border-top: 1px solid var(--color-border);
  background: var(--color-background);
}

.secondaryButton,
.primaryButton {
  height: var(--space-10);
  padding: 0 var(--space-5);
  border-radius: var(--radius-sm);
  font-family: var(--font-family-base);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
}

.secondaryButton {
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-dark);
}

.secondaryButton:hover {
  background: var(--color-surface-subtle);
}

.primaryButton {
  border: 1px solid var(--color-primary);
  background: var(--color-primary);
  color: var(--color-primary-text);
}

.primaryButton:hover {
  border-color: var(--color-primary-hover);
  background: var(--color-primary-hover);
}
